import { observer } from "mobx-react-lite";
import { type FC } from "react";
import { type FieldPath, type UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  Tags,
  TagsContent,
  TagsEmpty,
  TagsGroup,
  TagsItem,
  TagsList,
  TagsValue,
} from "@/components/ui/shadcn-io/tags";
import { cn } from "@/lib/utils";

import { CustomTagsTrigger } from "./CustomTagsTrigger";

interface MultiSelectWithTagsProps {
  tagsList: { id: string; name: string; group?: string }[];
  /* @ts-expect-error: incorrect type */
  form: UseFormReturn<T>;
  /* @ts-expect-error: incorrect type */
  nameSelect: FieldPath<T>;
  disabled?: boolean;
  classes?: string;
  placeholder?: string;
  emptyText?: string;
  grouped?: boolean;
  getGroupLabel?: (group: string) => string;
}

const MultiSelectWithTags: FC<MultiSelectWithTagsProps> = observer(
  ({
    tagsList,
    form,
    nameSelect,
    disabled,
    classes,
    placeholder = "Select multiple",
    emptyText = "No tags found.",
    grouped = false,
    getGroupLabel = (group: string) => group,
  }) => {
    return (
      <FormField
        control={form.control}
        name={nameSelect}
        render={({ field }) => {
          const cuisine: string[] = field.value ?? [];
          const availableTags = tagsList.filter(
            tag => !cuisine.includes(tag.id)
          );
          const tagGroups = grouped
            ? Object.entries(
                availableTags.reduce<Record<string, typeof availableTags>>(
                  (groups, tag) => {
                    const group = tag.group ?? "Other";
                    groups[group] ??= [];
                    groups[group].push(tag);
                    return groups;
                  },
                  {}
                )
              ).sort(([left], [right]) =>
                getGroupLabel(left).localeCompare(getGroupLabel(right))
              )
            : [["", availableTags] as const];

          const handleRemove = (value: string) => {
            field.onChange(cuisine.filter(v => v !== value));
          };

          const handleSelect = (value: string) => {
            if (cuisine.includes(value)) {
              handleRemove(value);
              return;
            }
            field.onChange([...cuisine, value]);
          };

          return (
            <FormItem className={cn(classes)}>
              <FormControl>
                <Tags>
                  <CustomTagsTrigger
                    title={cuisine.length === 0 ? placeholder : undefined}
                    disabled={disabled}
                    className={cuisine.length > 0 ? "py-1.5" : undefined}
                  >
                    {cuisine.map(tag => (
                      <TagsValue
                        key={tag}
                        onRemove={() => handleRemove(tag)}
                        className="bg-gray-100 text-sm text-gray-950"
                        data-testid={`multi-select-tag-selected-${tag}`}
                      >
                        {tagsList.find(t => t.id === tag)?.name}
                      </TagsValue>
                    ))}
                  </CustomTagsTrigger>

                  <TagsContent>
                    <TagsList>
                      <TagsEmpty>{emptyText}</TagsEmpty>
                      {tagGroups.map(([group, tags]) => (
                        <TagsGroup
                          key={group || "all"}
                          heading={group ? getGroupLabel(group) : undefined}
                        >
                          {tags.map(tag => (
                            <TagsItem
                              key={tag.id}
                              value={tag.id}
                              onSelect={handleSelect}
                              data-testid={`multi-select-tag-option-${tag.id}`}
                            >
                              {tag.name}
                            </TagsItem>
                          ))}
                        </TagsGroup>
                      ))}
                    </TagsList>
                  </TagsContent>
                </Tags>
              </FormControl>

              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  }
);

export default MultiSelectWithTags;
